import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Zap, Search } from "lucide-react";

const ENGINE_ROUTES = {
  crash: "/instant/crash",
  mines: "/instant/mines",
  dice: "/instant/dice",
  limbo: "/instant/limbo",
  plinko: "/instant/plinko",
  dragon: "/instant/dragon",
};

const ENGINE_LABELS = {
  crash: "Crash", mines: "Mines", dice: "Dice", limbo: "Limbo", plinko: "Plinko", dragon: "Dragon Tower",
};

export default function InstantLobby() {
  const nav = useNavigate();
  const [variants, setVariants] = useState([]);
  const [q, setQ] = useState("");
  const [engine, setEngine] = useState("");

  useEffect(() => {
    api.get("/instant/variants").then(r => setVariants(r.data.variants)).catch(() => {});
  }, []);

  const filtered = variants.filter(v => {
    if (engine && v.engine !== engine) return false;
    if (q && !v.name.toLowerCase().includes(q.toLowerCase()) && !v.theme.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const launch = (v) => {
    const route = ENGINE_ROUTES[v.engine];
    if (!route) return;
    const params = new URLSearchParams();
    Object.entries(v.preset || {}).forEach(([k, val]) => params.set(k, String(val)));
    nav(`${route}?${params.toString()}&variant=${v.id}`);
  };

  return (
    <div className="max-w-[1500px] mx-auto px-6 py-12">
      <div className="flex items-center gap-3 mb-2">
        <Zap className="text-[#d4af37]" size={32}/>
        <div>
          <h1 className="font-serif text-4xl">Instant Games</h1>
          <div className="text-white/50 text-sm mt-1">{variants.length} variants across 6 engines · Betmatch style</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 my-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16}/>
          <input data-testid="instant-search" value={q} onChange={e=>setQ(e.target.value)}
                 placeholder="Search variant..."
                 className="w-full bg-[#1a1a1a] border border-white/10 rounded pl-10 pr-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"/>
        </div>
        <div className="flex flex-wrap gap-1">
          <button onClick={() => setEngine("")}
                  className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest ${engine === "" ? "btn-gold" : "btn-ghost"}`}>All</button>
          {Object.entries(ENGINE_LABELS).map(([k, l]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setEngine(k)}
                    className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest ${engine === k ? "btn-gold" : "btn-ghost"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filtered.map(v => (
          <button key={v.id} data-testid={`variant-${v.id}`} onClick={() => launch(v)}
                  className="lux-card overflow-hidden group text-left">
            <div className={`aspect-[4/3] relative flex flex-col items-center justify-center p-3 bg-gradient-to-br ${v.color}`}>
              <div className="absolute inset-0 opacity-20"
                   style={{ backgroundImage: "linear-gradient(rgba(0,0,0,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.3) 1px, transparent 1px)", backgroundSize: "24px 24px" }}/>
              <div className="relative text-5xl mb-1 drop-shadow-lg">{v.theme.split(" ")[0]}</div>
              <div className="relative text-[10px] uppercase tracking-widest text-white/80 mt-1">{ENGINE_LABELS[v.engine]}</div>
            </div>
            <div className="p-3 border-t border-white/5">
              <h3 className="font-serif text-sm truncate group-hover:text-[#d4af37] transition">{v.name}</h3>
              <div className="text-[10px] text-white/40 mt-0.5 truncate">{v.tag}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
